import { useState, useEffect, useRef } from 'react';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ projects: 0, services: 0 });
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Count-up animation for stats
  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const increment = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setCounts({
        projects: Math.floor(20 * progress),
        services: Math.floor(4 * progress)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts({ projects: 20, services: 4 });
      }
    }, increment);

    return () => clearInterval(timer);
  }, [isVisible]);

  return (
    <section ref={sectionRef} id="about" className="py-20 lg:py-32 xl:py-40 bg-black">
      <div className="container mx-auto px-4 sm:px-6">
        
        {/* Main About Section - Image Left, Text Right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 xl:gap-24 items-center">
          
          {/* Image - Slide in from Left */}
          <div className={`transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            <div className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
              <img
                src="https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/82caf8bfc0c2ebad31fa3967515772f6.png"
                alt="Dion Bamme - Webdesign Entwickler"
                className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-700"
              />
            </div>
          </div>

          {/* Text Content - Slide in from Right */}
          <div className={`space-y-8 lg:space-y-12 transition-all duration-1000 delay-300 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            
            {/* Name & Title */}
            <div className="space-y-3 lg:space-y-4">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-bold text-white leading-none">
                Dion Bamme
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl text-[#d1bc8a] font-light">
                Webdesign & Entwicklung
              </p>
            </div>

            {/* Description */}
            <div className="space-y-5 lg:space-y-6 text-white/70 text-base lg:text-lg leading-relaxed border-l-2 border-[#d1bc8a] pl-6 lg:pl-8">
              <p>
                Ich bin Ihr persönlicher Webdesign-Entwickler mit Leidenschaft für moderne, 
                benutzerfreundliche Websites, die Ihre Kunden begeistern und Ihr Geschäft 
                erfolgreich im Internet präsentieren.
              </p>
              <p>
                Mit über 3 Jahren Erfahrung in der digitalen Welt helfe ich Unternehmen dabei, 
                ihre Online-Präsenz zu stärken und ihre Ziele durch maßgeschneiderte, 
                moderne Websites zu erreichen.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 lg:gap-8 pt-6 lg:pt-8 border-t border-white/10">
              <div className="space-y-1.5 lg:space-y-2">
                <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white">{counts.projects}+</div>
                <div className="text-white/50 text-xs sm:text-sm uppercase tracking-wider">Projekte</div>
              </div>
              <div className="space-y-1.5 lg:space-y-2">
                <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white">{counts.services}</div>
                <div className="text-white/50 text-xs sm:text-sm uppercase tracking-wider">Services</div>
              </div>
              <div className="space-y-1.5 lg:space-y-2">
                <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white">24/7</div>
                <div className="text-white/50 text-xs sm:text-sm uppercase tracking-wider">Support</div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}